import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-8279e54a"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "setup-fee-row" }
const _hoisted_2 = { class: "setup-fee-row" }

import { computed, inject, PropType, ref, watch } from "vue";
import { DxToolbarItem } from "devextreme-vue/popup";
import ModalBase from "@/components/ui/modals/modal-base.vue";
import { LookupTableSource } from "acquaint-api/constants/LookupTableSource";
import LookupSingleSelect from "@/components/ui/lookup/lookup-single-select.vue";
import { SetupFeeGridItem } from "@/types/tenancies/SetupFeeGridItem";
import CurrencyInput from "@/components/ui/inputs/currency-input.vue";
import CurrencyInfoBox from "@/components/ui/inputs/currency-info-box.vue";
import { ProductsValidation } from "acquaint-api/validation/ProductsValidation";
import { TenancyHelper } from "@/utils/tenancies/TenancyHelper";
import { AcquaintApiNextClient } from "@/services/AcquaintApiNextClient";
import displayErrorMessage from "@/utils/ErrorHandling";
import { getErrorMessage } from "bright-logic-core/src/errors";
import { SetupFeeInfo } from "@/types/tenancies/SetupFeeInfo";
import { AcquaintApiClient } from "@/services/AcquaintApiClient";
import { TaxRates } from "acquaint-api/models/TaxRates";

export default /*#__PURE__*/_defineComponent({
  __name: 'setup-fee-modal',
  props: {
  /**
   * The boolean value used with `v-model` to control the visibility or state of a modal or component.
   * @type {boolean}
   * @required
   */
  modelValue: {
    type: Boolean,
    required: true,
  },
  /**
   * The object of setup fee grid item passed from the parent component.
   * @type {SetupFeeGridItem}
   * @required: false
   */
  passedSetupFeeGridItem: {
    type: Object as PropType<SetupFeeGridItem>,
    required: false,
  },
},
  emits: [
  /**
   * Triggered when the `modelValue` changes. This is used to update the v-model variable in the parent component.
   * @property {boolean} value - Indicates whether the modal has been opened or closed.
   */
  "update:modelValue",
  /**
   * Triggered when a change occurs in the component, such as amount calculations or updates.
   * @property {SetupFeeGridItem} value - The details of the change, e.g., updated amounts or data.
   */
  "updateChange",
],
  setup(__props, { emit }) {

const props = __props;





const acquaintApiClient = inject("AcquaintApiClient") as AcquaintApiClient;
const acquaintNextApiClient = inject(
  "AcquaintApiNextClient",
) as AcquaintApiNextClient;

const initialDefaultSetupFeeGridItem = (): SetupFeeGridItem => ({
  productId: 0,
  productDescription: "",
  taxRate: 0,
  taxRateId: 0,
  defaultAmount: 0,
  net: 0,
  vat: 0,
  gross: 0,
});

const setupFeeGridItem = ref<SetupFeeGridItem>(
  initialDefaultSetupFeeGridItem(),
);

const showModal = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit("update:modelValue", value),
});

watch(
  () => showModal.value,
  async () => {
    if (!showModal.value) {
      return;
    }
    setupFeeGridItem.value = initialDefaultSetupFeeGridItem();
    if (props.passedSetupFeeGridItem) {
      setupFeeGridItem.value = { ...props.passedSetupFeeGridItem };
    }
  },
);

const enableAmountFields = computed(() => {
  return setupFeeGridItem.value.productId > 0;
});

const enableSave = computed(() => {
  return setupFeeGridItem.value.productId && setupFeeGridItem.value.gross > 0;
});

const fetchTaxRateById = async (id: number) => {
  try {
    const taxRateItem = await acquaintApiClient.httpGet<TaxRates>(
      `database/taxrates/${id}`,
    );
    return taxRateItem;
  } catch (err) {
    displayErrorMessage(getErrorMessage(err));
    return undefined;
  }
};

const fetchSetupFeeInfo = async (
  productId: number,
): Promise<SetupFeeInfo | undefined> => {
  try {
    if (productId <= 0) return undefined;

    const setupFee = await acquaintNextApiClient.httpGet<SetupFeeInfo>(
      `/tenancies/${productId}/Product/SetupFee`,
    );
    return setupFee;
  } catch (err) {
    displayErrorMessage(getErrorMessage(err));
    return undefined;
  }
};

const handleProductChange = async () => {
  if (setupFeeGridItem.value.productId === 0) return;

  const feeItem = await fetchSetupFeeInfo(setupFeeGridItem.value.productId);

  if (!feeItem) return;

  setupFeeGridItem.value = { ...setupFeeGridItem.value, ...feeItem };

  if (setupFeeGridItem.value.gross === 0) {
    setupFeeGridItem.value.gross = feeItem?.defaultAmount ?? 0;
  }
  setupFeeGridItem.value.taxRateId = feeItem?.taxRateId ?? 0;

  const taxRateItem = await fetchTaxRateById(setupFeeGridItem.value.taxRateId);
  setupFeeGridItem.value.taxRate = taxRateItem?.rate ?? 0;

  calculateAmounts(true);
};

const handleRateChange = async () => {
  const taxRateItem = await fetchTaxRateById(setupFeeGridItem.value.taxRateId);
  setupFeeGridItem.value.taxRate = taxRateItem?.rate ?? 0;

  calculateAmounts(true);
};

const handleNetChange = () => {
  calculateAmounts(false);
};
const handleGrossChange = () => {
  calculateAmounts(true);
};

const saveDetails = () => {
  emit("updateChange", setupFeeGridItem.value);
  showModal.value = false;
};
const calculateAmounts = (booGrossEntered: boolean) => {
  let decVAT: number = 0;

  if (booGrossEntered) {
    // Calculate figures based on gross amount
    decVAT = TenancyHelper.calcTax(
      setupFeeGridItem.value.gross,
      setupFeeGridItem.value.taxRate,
    );
    setupFeeGridItem.value.net = setupFeeGridItem.value.gross - decVAT;
  } else {
    // Calculate figures based on net amount
    decVAT =
      (setupFeeGridItem.value.net / 100) * setupFeeGridItem.value.taxRate;
    setupFeeGridItem.value.gross = setupFeeGridItem.value.net + decVAT;
  }
  setupFeeGridItem.value.vat = decVAT;
};

const cancelClick = () => {
  showModal.value = false;
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(ModalBase, {
    modelValue: showModal.value,
    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((showModal).value = $event)),
    title: "Item",
    height: "auto"
  }, {
    content: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(LookupSingleSelect, {
          class: "lookup-field",
          label: "Product",
          modelValue: setupFeeGridItem.value.productId,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((setupFeeGridItem.value.productId) = $event)),
          lookupSource: _unref(LookupTableSource).Products,
          showSearch: true,
          onValueChanged: handleProductChange
        }, null, 8, ["modelValue", "lookupSource"]),
        _createVNode(LookupSingleSelect, {
          class: "lookup-field",
          label: "Rate",
          modelValue: setupFeeGridItem.value.taxRateId,
          "onUpdate:modelValue": [
            _cache[1] || (_cache[1] = ($event: any) => ((setupFeeGridItem.value.taxRateId) = $event)),
            handleRateChange
          ],
          lookupSource: _unref(LookupTableSource).TaxRatesPercentage,
          showSearch: true
        }, null, 8, ["modelValue", "lookupSource"])
      ]),
      _createElementVNode("div", _hoisted_2, [
        _createVNode(CurrencyInput, {
          class: "lookup-field",
          modelValue: setupFeeGridItem.value.net,
          "onUpdate:modelValue": [
            _cache[2] || (_cache[2] = ($event: any) => ((setupFeeGridItem.value.net) = $event)),
            handleNetChange
          ],
          label: "Net",
          minValue: 0,
          maxValue: 99999999,
          fieldValidation: _unref(ProductsValidation).Price,
          "read-only": !enableAmountFields.value
        }, null, 8, ["modelValue", "fieldValidation", "read-only"]),
        _createVNode(CurrencyInfoBox, {
          class: "lookup-field",
          label: "VAT",
          value: setupFeeGridItem.value.vat
        }, null, 8, ["value"]),
        _createVNode(CurrencyInput, {
          class: "lookup-field",
          modelValue: setupFeeGridItem.value.gross,
          "onUpdate:modelValue": [
            _cache[3] || (_cache[3] = ($event: any) => ((setupFeeGridItem.value.gross) = $event)),
            handleGrossChange
          ],
          label: "Gross",
          minValue: 0,
          maxValue: 99999999,
          fieldValidation: _unref(ProductsValidation).Price,
          "read-only": !enableAmountFields.value
        }, null, 8, ["modelValue", "fieldValidation", "read-only"])
      ])
    ]),
    footer: _withCtx(() => [
      _createVNode(_unref(DxToolbarItem), {
        widget: "dxButton",
        toolbar: "bottom",
        location: "after",
        options: {
          text: 'Save',
          disabled: !enableSave.value,
          onClick: () => {
            saveDetails();
          },
        }
      }, null, 8, ["options"]),
      _createVNode(_unref(DxToolbarItem), {
        widget: "dxButton",
        toolbar: "bottom",
        location: "after",
        options: {
          text: 'Cancel',
          onClick: () => {
            cancelClick();
          },
        }
      }, null, 8, ["options"])
    ]),
    _: 1
  }, 8, ["modelValue"]))
}
}

})